/**
 * NB: This function encrypts strings on the web inquiry flow, which then get
 * decrypted via app/lib/util/inquiry_client_cipher.rb. Note that this is
 * using symmetric encryption so the key is shared amongst the two and we are
 * depending on obfuscation for the keys but this should not be a common pattern.
 * Please reach out to #engineering-security if you have any questions
 */
const INQUIRY_CLIENT_KEY = import.meta.env.VITE_INQUIRY_CLIENT_KEY;

const getSecretKey = () => {
  return crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(INQUIRY_CLIENT_KEY),
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt'],
  );
};

export const encryptPayload = async (data: string): Promise<string> => {
  const encoded = new TextEncoder().encode(data);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const secretKey = await getSecretKey();

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    secretKey,
    encoded,
  );

  const encryptedAsUint8Array = new Uint8Array(encrypted);
  const merged = new Uint8Array(encryptedAsUint8Array.length + iv.length);
  merged.set(encryptedAsUint8Array);
  merged.set(iv, encryptedAsUint8Array.length);

  return uint8ToBase64(merged);
};

type StringOrArrayBuffer<T> = T extends true ? string : ArrayBuffer;

export const decryptPayload = async <T extends boolean>(
  data: string,
  returnAsText: T,
): Promise<StringOrArrayBuffer<T>> => {
  const decoded = base64ToUInt8(data);

  const iv = decoded.slice(-12);
  const ciphertext = decoded.slice(0, decoded.length - 12);

  const secretKey = await getSecretKey();

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    secretKey,
    ciphertext,
  );

  return (
    returnAsText ? new TextDecoder('utf-8').decode(decrypted) : decrypted
  ) as StringOrArrayBuffer<T>;
};

function uint8ToBase64(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUInt8(base64string: string) {
  return Uint8Array.from(atob(base64string), (c) => c.charCodeAt(0));
}
